import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"
import { parseDateOnly, parseDateTime, formatDate, formatTime } from "../lib/datetime_format"

/**
 * Live recurrence summary for the events form.
 *
 * Renders a human-readable line that describes the current schedule
 * configuration, e.g. "Every Monday from May 4 through Dec 28 · 12:00–1:00 PM
 * · ~35 instances". Updates in real time as the user changes the event type,
 * frequency, dates/times, weekdays, until, count, or series parts. Pure
 * client-side; no fetch or Turbo round-trip.
 *
 * Usage in markup (inside the form):
 *
 *   <div data-controller="event-form-summary">
 *     <select data-event-form-summary-target="eventTypeInput"
 *             data-action="change->event-form-summary#update">…</select>
 *     <select data-event-form-summary-target="frequencyInput"
 *             data-action="change->event-form-summary#update">…</select>
 *     <input data-event-form-summary-target="startsAtInput"
 *            data-action="input->event-form-summary#update change->event-form-summary#update">
 *     <input data-event-form-summary-target="endsAtInput"
 *            data-action="input->event-form-summary#update change->event-form-summary#update">
 *     <input data-event-form-summary-target="intervalInput"
 *            data-action="input->event-form-summary#update">
 *     <input data-event-form-summary-target="recurrenceUntilInput"
 *            data-action="input->event-form-summary#update change->event-form-summary#update">
 *     <input data-event-form-summary-target="recurrenceCountInput"
 *            data-action="input->event-form-summary#update">
 *     <input data-event-form-summary-target="weekdayInput"
 *            data-action="change->event-form-summary#update" type="checkbox">
 *     <input data-event-form-summary-target="seriesPartCount" type="hidden">
 *     <p data-event-form-summary-target="summary"></p>
 *     <div data-event-form-summary-target="container">…</div>
 *   </div>
 */
export default class extends Controller {
  static targets = [
    "eventTypeInput",
    "frequencyInput",
    "startsAtInput",
    "endsAtInput",
    "intervalInput",
    "recurrenceUntilInput",
    "recurrenceCountInput",
    "weekdayInput",
    "seriesPartCount",
    "summary",
    "container",
  ]

  declare readonly hasEventTypeInputTarget: boolean
  declare readonly eventTypeInputTarget: HTMLSelectElement | HTMLInputElement
  declare readonly eventTypeInputTargets: Array<HTMLSelectElement | HTMLInputElement>
  declare readonly hasFrequencyInputTarget: boolean
  declare readonly frequencyInputTarget: HTMLSelectElement | HTMLInputElement
  declare readonly hasStartsAtInputTarget: boolean
  declare readonly startsAtInputTarget: HTMLInputElement
  declare readonly hasEndsAtInputTarget: boolean
  declare readonly endsAtInputTarget: HTMLInputElement
  declare readonly hasIntervalInputTarget: boolean
  declare readonly intervalInputTarget: HTMLInputElement
  declare readonly hasRecurrenceUntilInputTarget: boolean
  declare readonly recurrenceUntilInputTarget: HTMLInputElement
  declare readonly hasRecurrenceCountInputTarget: boolean
  declare readonly recurrenceCountInputTarget: HTMLInputElement
  declare readonly weekdayInputTargets: HTMLInputElement[]
  declare readonly hasSummaryTarget: boolean
  declare readonly summaryTarget: HTMLElement
  declare readonly hasSeriesPartCountTarget: boolean
  declare readonly seriesPartCountTarget: HTMLInputElement
  declare readonly hasContainerTarget: boolean
  declare readonly containerTarget: HTMLElement

  // Cap synthetic counts so an unintentional "weekly forever" doesn't
  // result in showing massive numbers.
  private static readonly MAX_ESTIMATED_INSTANCES = 500

  connect() {
    this.update()
  }

  update() {
    if (!this.hasSummaryTarget) return

    const eventType = this.currentEventType()

    if (eventType === "series") {
      this.summaryTarget.textContent = this.seriesSummary()
      this.showContainer(true)
      return
    }

    if (eventType === "one_time") {
      this.summaryTarget.textContent = this.oneTimeSummary()
      this.showContainer(false)
      return
    }

    // recurring
    this.summaryTarget.textContent = this.recurringSummary()
    this.showContainer(true)
  }

  private currentEventType(): string {
    if (!this.hasEventTypeInputTarget) return "one_time"

    // Segmented control (multiple radios): use the checked one's value.
    const radios = this.eventTypeInputTargets.filter(
      el => (el as HTMLInputElement).type === "radio",
    ) as HTMLInputElement[]
    if (radios.length > 0) {
      const checked = radios.find(r => r.checked)
      return (checked?.value || "one_time")
    }

    // Select-style fallback.
    return this.eventTypeInputTarget.value || "one_time"
  }

  private oneTimeSummary(): string {
    const parts = [i18n.t("events.form_summary.one_time")]
    const time = this.timeRange()
    if (time) parts.push(time)
    return parts.join(i18n.t("events.form_summary.separator"))
  }

  private seriesSummary(): string {
    const count = this.seriesPartsCount()
    return i18n.t("events.form_summary.series_parts", { count: count })
  }

  private recurringSummary(): string {
    const segments: string[] = []
    const frequency = this.currentFrequency()
    const interval = this.currentInterval()

    if (frequency) {
      segments.push(this.intervalLabel(frequency, interval))
    }

    if (frequency === "weekly" || frequency === "biweekly") {
      const weekdays = this.selectedWeekdayLabels()
      if (weekdays.length > 0) {
        segments.push(i18n.t("events.form_summary.on_days", { days: this.joinList(weekdays) }))
      }
    }

    const dateRange = this.dateRangeLabel()
    if (dateRange) segments.push(dateRange)

    const time = this.timeRange()
    if (time) segments.push(time)

    const occurrences = this.occurrenceCountLabel(frequency, interval)
    if (occurrences) segments.push(occurrences)

    return segments.join(i18n.t("events.form_summary.separator"))
  }

  private intervalLabel(frequency: string, interval: number): string {
    // biweekly is a fixed shape ("Every 2 weeks") in the locale file.
    if (frequency === "biweekly") {
      return i18n.t("events.form_summary.every_n_periods.biweekly")
    }
    return i18n.t(`events.form_summary.every_n_periods.${frequency}`, { count: interval })
  }

  private currentFrequency(): string {
    if (!this.hasFrequencyInputTarget) return ""
    return (this.frequencyInputTarget.value || "").trim()
  }

  private currentInterval(): number {
    if (!this.hasIntervalInputTarget) return 1
    const raw = parseInt(this.intervalInputTarget.value || "", 10)
    if (!isFinite(raw) || raw < 1) return 1
    return raw
  }

  private selectedWeekdayLabels(): string[] {
    if (this.weekdayInputTargets.length === 0) return []
    return this.weekdayInputTargets
      .filter(input => input.type === "checkbox" ? input.checked : Boolean(input.value))
      .map(input => {
        const day = input.value
        return i18n.t(`events.form_summary.weekdays.short.${day}`)
      })
      .filter(label => Boolean(label) && !label.startsWith("events.form_summary.weekdays"))
  }

  private joinList(items: string[]): string {
    return items.join(", ")
  }

  private dateRangeLabel(): string {
    const start = parseDateOnly(this.startsAtValue())
    const until = parseDateOnly(this.recurrenceUntilValue())

    if (!start) return ""
    if (until) {
      return i18n.t("events.form_summary.from_through", {
        start: formatDate(start),
        end: formatDate(until),
      })
    }
    return i18n.t("events.form_summary.from", { start: formatDate(start) })
  }

  private timeRange(): string {
    const start = parseDateTime(this.startsAtValue())
    const end = parseDateTime(this.endsAtValue())
    if (!start) return ""

    const startTime = formatTime(start)
    if (!end) return startTime
    return i18n.t("events.form_summary.time_range", {
      start: startTime,
      end: formatTime(end),
    })
  }

  private occurrenceCountLabel(frequency: string, interval: number): string {
    const until = parseDateOnly(this.recurrenceUntilValue())
    const explicitCount = this.parseInt(this.recurrenceCountValue())

    if (explicitCount && explicitCount > 0) {
      return i18n.t("events.form_summary.instance_count", { count: explicitCount })
    }

    if (until) {
      const start = parseDateOnly(this.startsAtValue())
      if (!start) return ""
      const estimate = this.estimateInstances(frequency, interval, start, until)
      if (estimate > 0) {
        return i18n.t("events.form_summary.instance_count_estimate", { count: estimate })
      }
    }

    return i18n.t("events.form_summary.indefinite")
  }

  private estimateInstances(frequency: string, interval: number, start: Date, end: Date): number {
    if (end.getTime() < start.getTime()) return 0
    const msPerDay = 24 * 60 * 60 * 1000
    const days = Math.floor((end.getTime() - start.getTime()) / msPerDay) + 1
    const stride = Math.max(1, interval)
    let raw = 0

    switch (frequency) {
      case "daily":
        raw = Math.floor(days / stride)
        break
      case "weekly": {
        const selectedDays = this.weekdayInputTargets.filter(i => i.type === "checkbox" ? i.checked : Boolean(i.value)).length
        const weeks = Math.floor(days / 7)
        const perWeek = selectedDays > 0 ? selectedDays : 1
        raw = Math.floor(weeks * perWeek / stride)
        break
      }
      case "biweekly":
        raw = Math.floor(days / 14)
        break
      case "monthly":
        raw = Math.floor(days / 30 / stride)
        break
      default:
        raw = 0
    }

    if (raw < 0) return 0
    return Math.min(raw, (this.constructor as typeof Object & { MAX_ESTIMATED_INSTANCES?: number }).MAX_ESTIMATED_INSTANCES ?? 500)
  }

  private seriesPartsCount(): number {
    if (this.hasSeriesPartCountTarget) {
      const v = this.parseInt(this.seriesPartCountTarget.value)
      if (v) return v
    }
    // Fallback: count visible series-part rows in the DOM rooted at the form.
    const rows = this.element.querySelectorAll<HTMLElement>("[data-series-part-row]")
    if (rows.length > 0) return rows.length
    // Last resort: assume 0
    return 0
  }

  private startsAtValue(): string {
    return this.hasStartsAtInputTarget ? this.startsAtInputTarget.value : ""
  }

  private endsAtValue(): string {
    return this.hasEndsAtInputTarget ? this.endsAtInputTarget.value : ""
  }

  private recurrenceUntilValue(): string {
    return this.hasRecurrenceUntilInputTarget ? this.recurrenceUntilInputTarget.value : ""
  }

  private recurrenceCountValue(): string {
    return this.hasRecurrenceCountInputTarget ? this.recurrenceCountInputTarget.value : ""
  }

  private parseInt(raw: string): number | null {
    if (!raw) return null
    const v = parseInt(raw, 10)
    return isFinite(v) ? v : null
  }

  private showContainer(visible: boolean) {
    if (!this.hasContainerTarget) return
    this.containerTarget.classList.toggle("hidden", !visible)
  }
}
